import { HubConnectionBuilder, LogLevel } from '@microsoft/signalr'
import { useAuthService } from '~/composables/useAuthService'
import { useRuntimeConfig } from 'nuxt/app'

const isConnected = ref(false)
let connection = null

// Map of eventName -> Set of listeners
const eventListeners = new Map()

export const useSignalR = () => {
    const connect = async (hubPath) => {
        if (process.server) return

        const config = useRuntimeConfig().public.atomNuxt
        const hubUrl = `${config.apiBaseUrl}${hubPath}`

        if (connection && connection.state === 'Connected') return

        const { getAccessToken } = useAuthService()
        const token = getAccessToken()
        if (!token) {
            console.warn('No access token found for SignalR connection')
            return
        }

        connection = new HubConnectionBuilder()
            .withUrl(hubUrl, { accessTokenFactory: () => token })
            .withAutomaticReconnect()
            .configureLogging(LogLevel.Information)
            .build()

        try {
            await connection.start()
            isConnected.value = true
            console.info('✅ SignalR connected')

            connection.onreconnected(() => {
                console.info('🔄 SignalR reconnected, re-registering event handlers')
                for (const eventName of eventListeners.keys()) {
                    registerSignalREventDispatcher(eventName)
                }
            })

            // Re-register master dispatchers
            for (const eventName of eventListeners.keys()) {
                registerSignalREventDispatcher(eventName)
            }
        } catch (err) {
            console.error('❌ SignalR connection failed:', err)
            isConnected.value = false
        }
    }

    const disconnect = async () => {
        if (connection && connection.state === 'Connected') {
            await connection.stop()
            isConnected.value = false
            console.info('👋 SignalR disconnected')
        }
    }

    const registerSignalREventDispatcher = (eventName) => {
        if (!connection) return
        connection.off(eventName) // prevent duplicates
        connection.on(eventName, (...args) => {
            const handlers = eventListeners.get(eventName)
            if (handlers) {
                for (const fn of handlers) {
                    try {
                        fn(...args)
                    } catch (err) {
                        console.error(`❌ Error in handler for '${eventName}'`, err)
                    }
                }
            }
        })
    }

    const on = (eventName, handler) => {
        if (!eventListeners.has(eventName)) {
            eventListeners.set(eventName, new Set())
            if (connection && connection.state === 'Connected') {
                registerSignalREventDispatcher(eventName)
            }
        }

        eventListeners.get(eventName).add(handler)
    }

    const off = (eventName, handler) => {
        if (!eventListeners.has(eventName)) return

        if (handler) {
            eventListeners.get(eventName).delete(handler)
        } else {
            eventListeners.set(eventName, new Set())
        }
    }

    const send = async (methodName, ...args) => {
        if (!connection || connection.state !== 'Connected') {
            console.warn('⚠️ Cannot send message — not connected')
            return
        }

        await connection.send(methodName, ...args)
    }

    return {
        isConnected,
        connect,
        disconnect,
        send,
        on,
        off
    }
}
